import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CharacterCount, Grid, TextField } from '@/shared/ui';
import {
  NOTES_MAX_LENGTH,
  REASON_FOR_VISIT_MAX_LENGTH,
} from '../utils/appointmentForm';

export type AppointmentTextFieldValues = {
  reasonForVisit: string;
  notes: string;
};

export type AppointmentTextFieldsProps = {
  reasonForVisit: string;
  notes: string;
  reasonError?: React.ReactNode;
  notesError?: React.ReactNode;
  reasonTouched?: boolean;
  notesTouched?: boolean;
  disabled?: boolean;
  reasonRequired?: boolean;
  notesDisabled?: boolean;
  notesPlaceholder?: string;
  /** Kept in sync on every keystroke so submit can flush without waiting for blur. */
  valuesRef?: React.MutableRefObject<AppointmentTextFieldValues>;
  /** Fires on every keystroke so parent can track dirty-before-blur. */
  onDraftChange?: (draft: AppointmentTextFieldValues) => void;
  onCommit: (field: 'reasonForVisit' | 'notes', value: string) => void;
};

/**
 * Local-state multiline fields so keystrokes do not re-render the parent form
 * (calendar, autocompletes, etc.). Commits to React Hook Form on blur.
 */
const AppointmentTextFields: React.FC<AppointmentTextFieldsProps> = ({
  reasonForVisit,
  notes,
  reasonError,
  notesError,
  reasonTouched = false,
  notesTouched = false,
  disabled = false,
  reasonRequired = true,
  notesDisabled,
  notesPlaceholder,
  valuesRef,
  onDraftChange,
  onCommit,
}) => {
  const { t } = useTranslation(['appointment']);
  const [reason, setReason] = useState(reasonForVisit);
  const [notesLocal, setNotesLocal] = useState(notes);

  useEffect(() => {
    setReason(reasonForVisit);
  }, [reasonForVisit]);

  useEffect(() => {
    setNotesLocal(notes);
  }, [notes]);

  useEffect(() => {
    const draft = { reasonForVisit: reason, notes: notesLocal };
    if (valuesRef) {
      valuesRef.current = draft;
    }
    onDraftChange?.(draft);
  }, [reason, notesLocal, valuesRef, onDraftChange]);

  return (
    <>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          id="reasonForVisit"
          name="reasonForVisit"
          label={t('fields.reasonForVisit')}
          labelEnd={<CharacterCount current={reason.length} max={REASON_FOR_VISIT_MAX_LENGTH} />}
          multiline
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          onBlur={() => onCommit('reasonForVisit', reason)}
          error={reasonTouched && Boolean(reasonError)}
          helperText={reasonTouched && reasonError}
          disabled={disabled}
          required={reasonRequired}
          placeholder={t('fields.reasonPlaceholder')}
          slotProps={{
            htmlInput: { maxLength: REASON_FOR_VISIT_MAX_LENGTH },
          }}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          id="notes"
          name="notes"
          label={t('fields.notes')}
          labelEnd={<CharacterCount current={notesLocal.length} max={NOTES_MAX_LENGTH} />}
          multiline
          rows={2}
          value={notesLocal}
          onChange={(e) => setNotesLocal(e.target.value)}
          onBlur={() => onCommit('notes', notesLocal)}
          error={notesTouched && Boolean(notesError)}
          helperText={notesTouched && notesError}
          disabled={notesDisabled ?? disabled}
          placeholder={notesPlaceholder ?? t('fields.notesPlaceholder')}
          slotProps={{
            htmlInput: { maxLength: NOTES_MAX_LENGTH },
          }}
        />
      </Grid>
    </>
  );
};

export default AppointmentTextFields;
